import type { DepartureDto } from "@tripcart/shared";

import { resolveDepartureStatus } from "./departure-status";

/** A departure's calendar year and month, read from the `YYYY-MM-DD` string
 *  rather than from a `Date`.
 *
 *  Parsing `"2026-03-14"` with `new Date()` yields a UTC midnight instant, and
 *  `getMonth()` on that reads the LOCAL month — which for anyone west of UTC is
 *  the previous day, and on the 1st of a month, the previous MONTH. A March
 *  departure would file itself under February for every traveller in the
 *  Americas. The string already carries the calendar date the operator meant;
 *  slicing it keeps it that way. */
function yearMonthOf(iso: string): { year: number; month: number } {
  return { year: Number(iso.slice(0, 4)), month: Number(iso.slice(5, 7)) - 1 };
}

/**
 * Which years, and which months within each, actually have departures.
 *
 * Only populated years and months are offered as tabs — an empty tab is a
 * dead end the traveller has to click to discover. A year tab showing the
 * full multi-year availability horizon (with its own "nothing scheduled yet"
 * empty state for the years with nothing in them) was tried and reverted: it
 * read as a broken filter rather than an honest one.
 */
export function groupByYearMonth(departures: DepartureDto[]): {
  years: number[];
  monthsByYear: Map<number, number[]>;
} {
  const monthsByYear = new Map<number, Set<number>>();
  for (const d of departures) {
    const { year, month } = yearMonthOf(d.date);
    const set = monthsByYear.get(year) ?? new Set<number>();
    set.add(month);
    monthsByYear.set(year, set);
  }
  const years = [...monthsByYear.keys()].sort((a, b) => a - b);
  return {
    years,
    monthsByYear: new Map(
      [...monthsByYear].map(([year, months]) => [
        year,
        [...months].sort((a, b) => a - b),
      ]),
    ),
  };
}

/**
 * One row per DATE, not one per (date, package).
 *
 * The availability API fans out per package, so a Saturday with three packages
 * arrives as three departure rows carrying the same date and three different
 * prices. Rendering them straight gives the traveller "Sat 18 Sep" three times
 * in a row, which reads as a bug, and no row on its own is the truth about the
 * date. The guide's list is one row per departure date; the package choice
 * happens AFTER a date is picked, which is exactly what this section's own
 * sub-heading promises.
 *
 * How each field aggregates, and why:
 *
 *  - open      — a date is bookable if ANY package on it is. Same rule as
 *                `collapseByDate` in `_availability-calendar.logic.ts`, so the
 *                calendar and this list can never disagree about a date.
 *  - spotsLeft — the largest count across OPEN packages, with `null`
 *                (uncapped) winning outright. Taking the smallest would call a
 *                date "1 spot left" because its most obscure package is nearly
 *                full, while another has ten.
 *  - priceFrom — the default package's (`isDefaultPackage`) while it is
 *                open, the tour card's "from" rule; otherwise the cheapest
 *                among OPEN packages. Quoting a sold-out package's lower
 *                price would advertise a number nobody can actually pay.
 *                Falls back to the cheapest overall only when nothing on
 *                the date is open, where the row shows "Sold out" and the
 *                figure is not offered anyway.
 */
export function collapseByDeparture(
  departures: DepartureDto[],
): DepartureDto[] {
  const byDate = new Map<string, DepartureDto[]>();
  for (const d of departures) {
    const rows = byDate.get(d.date);
    if (rows) rows.push(d);
    else byDate.set(d.date, [d]);
  }

  return [...byDate.values()].map((rows) => {
    const open = rows.filter(
      (d) => !d.isClosed && (d.spotsLeft === null || d.spotsLeft > 0),
    );
    const pool = open.length > 0 ? open : rows;
    const lead =
      pool.find((d) => d.isDefaultPackage) ??
      pool.reduce((best, d) =>
        d.priceFrom.amount < best.priceFrom.amount ? d : best,
      );
    // `null` beats any number: one uncapped package leaves the DATE uncapped.
    const spotsLeft = open.some((d) => d.spotsLeft === null)
      ? null
      : open.reduce((max, d) => Math.max(max, d.spotsLeft ?? 0), 0);

    return {
      ...lead,
      // Key by date — the row now represents the date, not one package's row,
      // and `packageId` on the spread is whichever package set the price.
      id: lead.date,
      isClosed: open.length === 0,
      spotsLeft: open.length === 0 ? 0 : spotsLeft,
    };
  });
}

export interface ListOptions {
  year: number;
  month: number | "all";
  sort: "date" | "price" | "seats";
}

/**
 * Which sort chips are worth offering for THIS schedule.
 *
 * A sort that cannot change the order is a control that answers every click by
 * redrawing the same list. Both of the optional two are offered only when the
 * data behind them varies:
 *
 *  - `price` — only when the departures do not all cost the same, i.e. at
 *    least one is cheaper than the dearest. On a flat-priced tour every row
 *    carries one number and "Lowest price" is the date order with extra steps.
 *  - `seats` — only when at least one departure publishes a real remaining
 *    count. An uncapped schedule reports `spotsLeft: null` on every row, and
 *    "Fewest seats left" then ranks nothing.
 *
 * `date` is always offered: a schedule always has an order in time.
 *
 * Judged on the COLLAPSED rows, the same ones the list renders — a tour whose
 * packages differ in price but whose dates do not would otherwise look varied
 * here and flat on screen.
 */
export function availableSorts(
  departures: DepartureDto[],
): ListOptions["sort"][] {
  const rows = collapseByDeparture(departures);
  const prices = new Set(rows.map((d) => d.priceFrom.amount));
  return [
    "date",
    ...(prices.size > 1 ? (["price"] as const) : []),
    // OPEN rows only. `collapseByDeparture` writes `spotsLeft: 0` on a fully
    // closed date to mean "closed", not "nought seats published" — so on an
    // uncapped schedule a single sold-out date was enough to offer a sort that
    // had nothing to rank, which is the opposite of what the note above says.
    ...(rows.some((d) => !d.isClosed && d.spotsLeft !== null)
      ? (["seats"] as const)
      : []),
  ];
}

/**
 * The visible list: filtered to the chosen year/month, then ordered.
 *
 * THREE sort keys, and all three are true sorts — `date`, `price`, `seats`.
 * Nothing here narrows the list beyond the year/month the traveller picked.
 *
 * This docstring described a fourth and fifth key, `low` and `critical`, as
 * "FILTERS wearing a sort's clothing" that narrow to dates running out, and
 * promised a caller message for when none matched. None of that exists:
 * `ListOptions["sort"]` is `date | price | seats`, and there is no such
 * message. `seats` is what replaced them — it RANKS by fewest remaining
 * without dropping a single date, so nobody loses a departure to a threshold
 * we chose for them.
 */
export function filterAndSort(
  departures: DepartureDto[],
  { year, month, sort }: ListOptions,
): DepartureDto[] {
  // Collapse BEFORE filtering, so "Filling up fast" is judged on the date's
  // real aggregate rather than on one package's row.
  const inWindow = collapseByDeparture(departures).filter((d) => {
    const ym = yearMonthOf(d.date);
    if (ym.year !== year) return false;
    return month === "all" || ym.month === month;
  });

  return [...inWindow].sort((a, b) => {
    if (sort === "price") {
      const diff = a.priceFrom.amount - b.priceFrom.amount;
      // Same price → soonest first, so the order is stable and useful rather
      // than whatever the API happened to return.
      if (diff !== 0) return diff;
    }
    if (sort === "seats") {
      // Fewest remaining first. An UNCAPPED date has no count and cannot be
      // "running out", so it sorts last rather than first — treating `null` as
      // zero would put every unlimited departure at the top of a list whose
      // whole purpose is to surface the scarce ones.
      // A large FINITE sentinel, not `Infinity`.
      //
      // Every row without a real seat count has to sort below every row that
      // has one, and `Infinity` expresses that — but it does not SUBTRACT.
      // Two such rows give `Infinity - Infinity` = `NaN`, which is not `0`, so
      // a `diff !== 0` guard returned NaN; `Array#sort` coerces that to `+0`
      // and the date tiebreak never ran. An operator who caps nothing has a
      // list where every pair is that pair, so "Fewest seats left" left the
      // whole thing in Map-insertion order.
      //
      // Guarding with `Number.isFinite(diff)` looks like the fix and is not:
      // `6 - Infinity` is `-Infinity`, equally non-finite, so it would also
      // skip the comparisons that are meant to rank. (That version shipped
      // briefly and a spec caught it.) Keeping the arithmetic finite means
      // ordinary subtraction works for every pair.
      const rank = (d: DepartureDto): number => {
        const status = resolveDepartureStatus(d);
        if (status.kind === "seats") return status.spotsLeft;
        return Number.MAX_SAFE_INTEGER;
      };
      const diff = rank(a) - rank(b);
      if (diff !== 0) return diff;
    }
    return a.date.localeCompare(b.date);
  });
}
